/**
 * Create Project idempotency.
 *
 * One attempt has one creation_idempotency_key (material-batch / intake style).
 * The in-flight lock stops a second insert before the first await. The partial
 * unique index stops a second row if that response is lost and the same key is
 * sent again. Business fields are not part of the key.
 */

import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
} from "./orgQueryScope";
import { isUuidString } from "./uuidFields";

export const PROJECT_CREATION_IDEMPOTENCY_COLUMN = "creation_idempotency_key";

export const PROJECT_CREATION_IDEMPOTENCY_CONSTRAINT =
  "uq_projects_creation_idempotency_key";

export const PROJECT_SOURCE_ESTIMATE_COLUMN = "source_estimate_id";

export const PROJECT_SOURCE_ESTIMATE_CONSTRAINT =
  "uq_projects_source_estimate_id";

function uniqueViolationText(error) {
  return [error?.message, error?.details, error?.hint, error?.constraint]
    .filter(Boolean)
    .join(" ");
}

export function isUniqueViolation(error) {
  return String(error?.code || "") === "23505";
}

/**
 * Which projects unique conflict this insert hit, if it is one we can resolve.
 * Returns the column to look up, or null when the error must surface as-is.
 */
export function classifyProjectUniqueViolation(error) {
  if (!isUniqueViolation(error)) {
    return null;
  }

  const text = uniqueViolationText(error);

  if (
    text.includes(PROJECT_CREATION_IDEMPOTENCY_CONSTRAINT) ||
    text.includes(PROJECT_CREATION_IDEMPOTENCY_COLUMN)
  ) {
    return PROJECT_CREATION_IDEMPOTENCY_COLUMN;
  }

  if (
    text.includes(PROJECT_SOURCE_ESTIMATE_CONSTRAINT) ||
    text.includes(PROJECT_SOURCE_ESTIMATE_COLUMN)
  ) {
    return PROJECT_SOURCE_ESTIMATE_COLUMN;
  }

  return null;
}

function defaultCreateId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  throw new Error("crypto.randomUUID is required to create a project.");
}

/**
 * Token for a single Create Project attempt.
 * token() creates once and then reuses it. startNewAttempt() is a new form lifecycle.
 * adopt() restores the key persisted with an in-progress draft.
 */
export function createProjectCreationSession(createId = defaultCreateId) {
  let token = null;

  return {
    peek() {
      return token;
    },
    token() {
      if (!token) {
        token = createId();
      }
      return token;
    },
    adopt(value) {
      if (!isUuidString(value)) {
        return null;
      }
      token = String(value).trim();
      return token;
    },
    startNewAttempt() {
      token = createId();
      return token;
    },
  };
}

/**
 * Synchronous in-flight guard. tryAcquire() flips the flag before any await
 * so two callers in the same turn cannot both proceed.
 */
export function createInFlightSubmissionLock() {
  let inFlight = false;

  return {
    get inFlight() {
      return inFlight;
    },
    tryAcquire() {
      if (inFlight) {
        return false;
      }
      inFlight = true;
      return true;
    },
    release() {
      inFlight = false;
    },
  };
}

/**
 * On a known unique conflict, return the existing org-scoped project.
 * Returns null when the conflict is unknown or the row is not visible in this org.
 */
export async function resolveIdempotentProjectInsert(
  client,
  actor,
  projectPayload,
  error,
  context = {},
) {
  const column = classifyProjectUniqueViolation(error);
  if (!column || !client) {
    return null;
  }

  const value = projectPayload?.[column];
  if (!isUuidString(value)) {
    return null;
  }

  const organizationId = await resolveOrganizationIdForQueries(
    actor,
    client,
    context.organizationId,
  );

  let query = client.from("projects").select("project_id").eq(column, value);
  query = applyOrganizationScope(query, organizationId, { tableName: "projects" });

  const { data, error: lookupError } = await query.maybeSingle();
  if (lookupError || !data?.project_id) {
    return null;
  }

  return {
    data,
    error: null,
    idempotentReplay: true,
  };
}
